"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const Model_1 = require("../Model");
const ModularScaleExceptions_1 = require("./ModularScaleExceptions");
const String_1 = require("@pups/utility/build/String");
const Number_1 = require("@pups/utility/build/Number");
const ModularScaleDefaults_1 = require("./ModularScaleDefaults");
class ModularScale extends Model_1.default {
    /**
     * Constructor
     */
    constructor(base = ModularScaleDefaults_1.DEFAULT_MODULAR_SCALE_BASE, unit = ModularScaleDefaults_1.DEFAULT_MODULAR_SCALE_UNIT, ratio = ModularScaleDefaults_1.DEFAULT_MODULAR_SCALE_RATIO, rootBase = ModularScaleDefaults_1.DEFAULT_MODULAR_SCALE_ROOT_BASE) {
        super();
        /**
         * Proprietà da esportare nel file di configuratione
         *
         * @type {Array<string>}
         * @memberof ModularScale
         */
        this.exportableProperties = ['base', 'unit', 'ratio'];
        this.setBase(base);
        this.setUnit(unit);
        this.setRatio(ratio);
        this.setRootBase(rootBase);
    }
    setRootBase(rootBase) {
        if (rootBase.indexOf('px') < 0)
            throw new ModularScaleExceptions_1.RootBaseNotValidException();
        this.rootBase = parseFloat(rootBase);
    }
    getRootBase(forExport) {
        return forExport ? this.rootBase + 'px' : this.rootBase;
    }
    setBase(base) {
        this.base = typeof base === 'string' ? parseFloat(base) : base;
    }
    getBase() {
        return this.base;
    }
    /**
     * Set ModularScale ratio
     *
     * @param ratio
     */
    setRatio(ratio) {
        if (typeof ratio === 'string') {
            if (Object.keys(ModularScale.RATIOS).indexOf(ratio) < 0)
                throw new ModularScaleExceptions_1.RatioNotValidException(ModularScale.RATIOS);
            this.ratio = ModularScale.RATIOS[ratio];
        }
        else {
            this.ratio = ratio;
        }
    }
    getRatio() {
        return this.ratio;
    }
    setUnit(unit) {
        if (ModularScale.getUnits().indexOf(unit) < 0)
            throw new ModularScaleExceptions_1.UnitNotValidException(unit, ModularScale.UNITS);
        this.unit = unit;
    }
    getUnit() {
        return this.unit;
    }
    /**
     * Ritorna il valore scalato
     */
    get(increment) {
        return ModularScale.get(increment, this.base, this.unit, this.ratio);
    }
    /**
     * Ritorna il valore scalato (statico)
     */
    static get(increment, base, unit, ratio) {
        let value = base;
        for (let i = Math.abs(increment); i > 0; i--)
            value = increment > 0 ? value * ratio : value / ratio;
        return value + unit;
    }
    static getUnits() {
        return ModularScale.UNITS;
    }
    /**
     * Ritorna l'unità di misura di una stringa
     */
    static getUnitFromString(value, fallback) {
        if (typeof value === 'string') {
            const unit = value.replace(parseFloat(value).toString(), '').toLowerCase();
            return ModularScale.UNITS.indexOf(unit) >= 0 ? unit : ModularScaleDefaults_1.DEFAULT_MODULAR_SCALE_UNIT;
        }
        return fallback || ModularScaleDefaults_1.DEFAULT_MODULAR_SCALE_UNIT;
    }
    /*
     |--------------------------------------------------------------------------
     | Operations
     |--------------------------------------------------------------------------
     */
    operation(type, args) {
        const len = args.length;
        const unit = this.getUnit();
        const first = this.resolveValue(args[0], unit);
        let result = parseFloat(first);
        for (let i = 1; i < len; i++) {
            const current = this.resolveValue(args[i], unit);
            switch (type) {
                case '+':
                    result += parseFloat(current);
                    break;
                case '-':
                    result -= parseFloat(current);
                    break;
                case '*':
                    result *= parseFloat(current);
                    break;
                case '/':
                    result /= parseFloat(current);
                    break;
            }
        }
        return result + unit;
    }
    resolveValue(value, toUnit) {
        if (typeof value === 'number')
            return this.get(value);
        const unit = ModularScale.getUnitFromString(value);
        if (!toUnit || unit != toUnit) {
            const method = `${unit}To${String_1.ucfirst(toUnit)}`;
            return this[method](value);
        }
        return value;
    }
    add(...args) {
        return this.operation('+', args);
    }
    sub(...args) {
        return this.operation('-', args);
    }
    mul(...args) {
        return this.operation('*', args);
    }
    div(...args) {
        return this.operation('/', args);
    }
    /*
     |--------------------------------------------------------------------------
     | Scales utility
     |--------------------------------------------------------------------------
     */
    /**
     * Ritorna il valore n-esimo di fibonacci
     */
    static fib(n = null) {
        return n <= 2 ? 1 : Math.round(Math.pow(ModularScaleDefaults_1.PHI, n) / ModularScaleDefaults_1.SQRT_5);
    }
    /**
     * Ritorna il valore della sezione aurea
     */
    static gs(i) {
        return ModularScale.GOLDEN_SECTIONS['GLD' + (i - 1)];
    }
    /*
     |--------------------------------------------------------------------------
     | Converions
     |--------------------------------------------------------------------------
     */
    valueToUnit(value, unit) {
        return this.resolveValue(value, unit);
    }
    remToPx(rem) {
        return ModularScale.remToPx(rem, this.rootBase);
    }
    remToPt(rem) {
        return ModularScale.remToPt(rem, this.rootBase);
    }
    emToPt(em) {
        return ModularScale.emToPt(em);
    }
    emToPx(em) {
        return ModularScale.emToPx(em);
    }
    pxToRem(px) {
        return ModularScale.pxToRem(px, this.rootBase);
    }
    pxToEm(px) {
        return ModularScale.pxToEm(px);
    }
    pxToPt(px) {
        return ModularScale.pxToPt(px);
    }
    ptToPx(pt) {
        return ModularScale.ptToPx(pt);
    }
    ptToRem(pt) {
        return ModularScale.ptToRem(pt, this.rootBase);
    }
    ptToEm(pt) {
        return ModularScale.ptToEm(pt);
    }
    static remToPx(rem, rootBase) {
        return Number_1.fix(Number_1.fix(rem) * rootBase) + 'px';
    }
    static remToPt(rem, rootBase) {
        return ModularScale.pxToPt(ModularScale.remToPx(rem, rootBase));
    }
    static emToPt(em) {
        return ModularScale.pxToPt(ModularScale.emToPx(em));
    }
    static emToPx(em) {
        return Number_1.fix(Number_1.fix(em) * 16) + 'px';
    }
    static pxToRem(px, rootBase) {
        return (Number_1.fix(px) / rootBase) + 'rem';
    }
    static pxToEm(px) {
        return (Number_1.fix(px) / 16) + 'em';
    }
    static pxToPt(px) {
        return (Number_1.fix(px) * 0.75) + 'pt';
    }
    static ptToPx(pt) {
        return (Number_1.fix(pt) * (12 / 9)) + 'px';
    }
    static ptToRem(pt, rootBase) {
        return ModularScale.pxToRem(ModularScale.ptToPx(pt), rootBase);
    }
    static ptToEm(pt) {
        return ModularScale.pxToEm(ModularScale.ptToPx(pt));
    }
}
/**
 * Unità possibili per i css
 */
ModularScale.UNITS = ['', 'em', 'ex', '%', 'px', 'cm', 'mm', 'in', 'pt', 'pc', 'ch', 'rem', 'vh', 'vw', 'vmin', 'vmax'];
/**
 * Ratios di base
 */
ModularScale.RATIOS = {
    'MINOR_SECOND': 1.067, 'MAJOR_SECOND': 1.125, 'MINOR_THIRD': 1.2, 'MAJOR_THIRD': 1.25,
    'PERFECT_FOURTH': 1.333, 'AUGMENTED_FOURTH': 1.414, 'PERFECT_FIFTH': 1.5, 'MINOR_SIXTH': 1.6,
    'PHI': ModularScaleDefaults_1.PHI, 'GOLDEN': ModularScaleDefaults_1.PHI, 'MAJOR_SIXTH': 1.667, 'MINOR_SEVENTH': 1.778, 'MAJOR_SEVENTH': 1.875,
    'OCTAVE': 2, 'MAJOR_TENTH': 2.5, 'MAJOR_ELEVENTH': 2.667, 'MAJOR_TWELFTH': 3, 'DOUBLE_OCTAVE': 4
};
/**
 * Sezione aurea
 *
 * @protected
 * @static
 * @type {Object}
 * @memberof ModularScale
 */
ModularScale.GOLDEN_SECTIONS = { GLD10: ModularScaleDefaults_1.GLD10, GLD9: ModularScaleDefaults_1.GLD9, GLD8: ModularScaleDefaults_1.GLD8, GLD7: ModularScaleDefaults_1.GLD7, GLD6: ModularScaleDefaults_1.GLD6, GLD5: ModularScaleDefaults_1.GLD5, GLD4: ModularScaleDefaults_1.GLD4, GLD3: ModularScaleDefaults_1.GLD3, GLD2: ModularScaleDefaults_1.GLD2, GLD1: ModularScaleDefaults_1.GLD1 };
exports.default = ModularScale;
//# sourceMappingURL=ModularScale.js.map